/**
 * WelcomeScreen.js - Écran d'accueil sobre et professionnel
 */
import "./welcome-screen.css";
import { createElement } from "../../utils/dom";
import { getSwVersion } from "../../../main";

export class WelcomeScreen {
  constructor(parentElement, options = {}) {
    if (!parentElement) {
      throw new Error("WelcomeScreen: parentElement est requis");
    }

    this.parentElement = parentElement;
    this.options = {
      onCreateProject: options.onCreateProject || (() => {}),
      onLoadProject: options.onLoadProject || (() => {}),
      ...options,
    };

    this.container = null;
    this.render();
  }

  /**
   * Créer le container du composant
   */
  createContainer() {
    this.container = createElement("div", {
      className: "ui-container-center",
    });

    this.parentElement.appendChild(this.container);
    return this.container;
  }

  /**
   * Rendu de l'écran d'accueil
   */
  render() {
    if (!this.container) {
      this.createContainer();
    }

    this.container.innerHTML = `
    <div class="ui-card ui-card-wide">
      <div class="welcome-header">
        <h1 class="welcome-title">Configuration des Tiroirs Fibres Optiques</h1>
        <p class="welcome-subtitle">
          Préparez la configuration de vos PM en définissant le type de module (12 ou 24 fibres), le nombre de modules par tiroir, 
          ainsi que le nombre total de tiroirs. La configuration peut se faire manuellement ou à l'aide de la reconnaissance vocale.
        </p>
      </div>

      <div class="welcome-how-it-works">
        <h2 class="how-it-works-title">❓ Comment ça marche ?</h2>
        <p class="how-it-works-text">
          Créez un nouveau projet ou importez-en un existant, puis suivez les différentes étapes de configuration. Affectez à chaque fibre un état 
          parmi : Libre, Occupé ou N/A, module par module. Enfin, exportez la configuration.
        </p>
      </div>

      <div class="welcome-actions">
        <button class="ui-btn ui-btn-outline" id="loadProjectBtn">
          <span class="btn-icon">📂</span>
          <span class="btn-text">Charger un ancien projet</span>
        </button>
        
        <button class="ui-btn ui-btn-primary" id="createProjectBtn">
          <span class="btn-icon">✨</span>
          <span class="btn-text">Créer un nouveau projet</span>
        </button>
      </div>

      <div class="welcome-footer">
        <div class="footer-links">
          <a href="mailto:hello@ite-technologies.fr" class="footer-link">hello@ite-technologies.fr</a>
          <a href="https://www.ite-technologies.fr" target="www.ite-technologies.fr" class="footer-link">www.ite-technologies.fr</a>
        </div>
        <div class="footer-version">${getSwVersion()}</div>
      </div>
    </div>
  `;

    this.setupEventListeners();
  }

  /**
   * Configurer les événements
   */
  setupEventListeners() {
    const createBtn = this.container.querySelector("#createProjectBtn");
    const loadBtn = this.container.querySelector("#loadProjectBtn");

    if (createBtn) {
      createBtn.addEventListener("click", () => {
        this.options.onCreateProject();
      });
    }

    if (loadBtn) {
      loadBtn.addEventListener("click", () => {
        this.options.onLoadProject();
      });
    }
  }

  /**
   * Animer l'apparition de l'écran
   */
  animate() {
    const container = this.container.querySelector(".ui-card");
    if (container) {
      container.style.opacity = "0";
      container.style.transform = "translateY(20px)";
      container.style.transition = "all 0.6s ease-out";

      requestAnimationFrame(() => {
        container.style.opacity = "1";
        container.style.transform = "translateY(0)";
      });
    }
  }

  /**
   * Masquer l'écran d'accueil
   */
  hide() {
    if (this.container) {
      this.container.style.display = "none";
    }
  }

  /**
   * Afficher l'écran d'accueil
   */
  show() {
    if (this.container) {
      this.container.style.display = "block";
      this.animate();
    }
  }

  /**
   * Obtenir l'élément DOM du composant
   */
  getElement() {
    return this.container;
  }

  /**
   * Vérifier si le composant est monté
   */
  isMounted() {
    return this.container && this.container.parentNode === this.parentElement;
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  /**
   * Nettoyer les ressources
   */
  destroy() {
    // Supprimer les événements
    const buttons = this.container?.querySelectorAll(".welcome-btn");
    buttons?.forEach((btn) => {
      btn.removeEventListener("click", btn.onclick);
    });

    // Démonter le composant
    this.unmount();

    // Nettoyer les références
    this.container = null;
    this.parentElement = null;
  }
}
